import { Builder } from "@builder.io/react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CustomBreadcrumb } from "@/components/Breadcrumb";
import { sectionVariants, sectionSpacing, buttonStyle, registerMenuGroups, commonInputs, allowedFileTypes } from "./helper";
import { NotFound } from "@/components/404";
import { PageNav } from "@/components/Sections";

const pageComponents = [
  {
    name: "Page Components",
    items: [
      { name: "Header" },
      { name: "Breadcrumb" },
      { name: "Footer" },
      { name: "PageNav" },
      { name: "Not Found" }
    ]
  },
];

export const registerPageComponents = () => {
  registerMenuGroups(pageComponents);

  // Header
  Builder.registerComponent(Header, {
    name: "Header",
    noWrap: true,
    hideFromInsertMenu: true,
    inputs: [
      {
        name: "darkMode",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "variant",
        type: "string",
        enum: sectionVariants,
        defaultValue: "A",
      },
      {
        name: "showAnnouncementBar",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "announcementBarDismissable",
        type: "boolean",
      },
      {
        name: "announcementBarTitle",  
        type: "string",
      },
      {
        name: "announcementBarContent",
        type: "richText",
      },
      {
        name: "announcementBarHasButton",
        type: "boolean",
      },
      {
        name: "announcementBarButtonText",
        type: "string",
        showIf: `options.get('announcementBarHasButton') === true`,
      },
      {
        name: "announcementBarButtonLink",
        type: "url",
        showIf: `options.get('announcementBarHasButton') === true`,
      },
      {
        name: "announcementBarButtonStyle",
        type: "string",
        enum: buttonStyle,
        showIf: `options.get('announcementBarHasButton') === true`,
      },
      {
        name: "showNav",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "customAccountArea",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "withPartnerLogo",
        type: "boolean",
        defaultValue: false,
        showIf: `options.get('customAccountArea') === true`,
      },
      {
        name: "partnerLogo",
        type: "file",
        allowedFileTypes: allowedFileTypes,
        showIf: `options.get('withPartnerLogo') === true`,
      },
      {
        name: "customText",
        type: "richText",
        showIf: `options.get('customAccountArea') === true`,
      },
      {
        name: "customButtonText",
        type: "string",
        showIf: `options.get('customButton') === true`,
      },
      {
        name: "customButtonLink",
        type: "url",
        showIf: `options.get('customButton') === true`,
      },
      {
        name: "customButtonStyle",
        type: "string",
        showIf: `options.get('customButton') === true`,
        enum: buttonStyle,
      },
      {
        name: "accountArea",
        type: "object",
        showIf: `options.get('customAccountArea') === false`,
        subFields: [
          {
            name: "loginButton",
            type: "boolean",
            defaultValue: true,
          },
          {
            name: "loginLink",
            type: "url",
            showIf: `options.get('loginButton') === true`,
          },
          {
            name: "loginText",
            type: "string",
            showIf: `options.get('loginButton') === true`,
          },
          {
            name: "enquiryButton",
            type: "boolean",
            defaultValue: true,
          },
          {
            name: "enquiryLink",
            type: "url",
            showIf: `options.get('enquiryButton') === true`,
          },
          {
            name: "enquiryText",
            type: "string",
            showIf: `options.get('enquiryButton') === true`,
          },
        ],
      },
      {
        name: "menuItem",
        type: "list",
        subFields: [
          {
            name: "label",
            type: "string",
          },
          {
            name: "hasSubmenu",
            type: "boolean",
            defaultValue: true,
          },
          {
            name: "link",
            type: "url",
            showIf: `options.get('hasSubmenu') === false`,
          },
          {
            name: "subMenu",
            type: "object",
            showIf: `options.get('hasSubmenu') === true`,
            subFields: [
              {
                name: "hasInfoBlock",
                type: "boolean",
                defaultValue: true,
              },
              {
                name: "infoBlock",
                type: "object",
                subFields: [
                  ...commonInputs.menuItemInputs ({
                    includeTitle: true,
                    includeSubtitle: true,
                    includeLink: true,
                    includeButton: true,
                  }),
                ],
                showIf: `options.get('hasInfoBlock') === true`,
              },
              {
                name: "hasFeatureBlock",
                type: "boolean",
                defaultValue: true,
              },
              {
                name: "featureBlock",
                showIf: `options.get('hasFeatureBlock') === true`,
                type: "object",
                subFields: [
                  {
                    name: "item",
                    type: "object",
                    subFields: [
                      ...commonInputs.menuItemInputs({
                        includeTitle: true,
                        includeSubtitle: true,
                        includeLink: true,
                        includeImage: true,
                        includeButton: true,
                      }),
                    ],
                  },
                ],
              },
              {
                name: "hasLeftColItems",
                type: "boolean",
                defaultValue: true,
              },
              {
                name: "leftColItems",
                type: "object",
                showIf: `options.get('hasLeftColItems') === true`,
                subFields: [
                  {
                    name: "label",
                    type: "string",
                  },
                  {
                    name: "displayStyle",
                    type: "string",
                    enum: [
                      "Feature Icon Item",
                      "List Icon Item",
                      "Feature Image Item",
                      "Plain Item",
                    ],
                  },
                  {
                    name: "items",
                    type: "list",
                    subFields: [
                      ...commonInputs.menuItemInputs({
                        includeTitle: true,
                        includeSubtitle: true,
                        includeLink: true,
                        includeImage: true,
                        includeButton: true,
                        includeSuffixIcon: true,
                      }),
                    ],
                  },
                ],
              },
              {
                name: "hasRightColItems",
                type: "boolean",
                defaultValue: true,
              },
              {
                name: "rightColBorderBefore",
                type: "boolean",
                defaultValue: false,
                showIf: `options.get('hasRightColItems') === true`,
              },
              {
                name: "rightColReducedWidth",
                type: "boolean",
                defaultValue: false,
                showIf: `options.get('hasRightColItems') === true`,
              },
              {
                name: "rightColItems",
                type: "object",
                showIf: `options.get('hasRightColItems') === true`,
                subFields: [
                  {
                    name: "label",
                    type: "string",
                  },
                  {
                    name: "displayStyle",
                    type: "string",
                    enum: [
                      "List Icon Item",
                      "Feature Icon Item",
                      "Feature Image Item",
                      "Plain Item",
                    ],
                  },
                  {
                    name: "items",
                    type: "list",
                    subFields: [
                      ...commonInputs.menuItemInputs({
                        includeTitle: true,
                        includeSubtitle: true,
                        includeLink: true,
                        includeImage: true,
                        includeButton: true,
                        includeSuffixIcon: true,
                      }),
                    ],
                  },
                ],
              },
              {
                name: "hasFooterMenuItems",
                type: "boolean",
                defaultValue: true,
              },
              {
                name: "footerMenuItems",
                type: "list",
                subFields: [
                  ...commonInputs.menuItemInputs({
                    includeTitle: true,
                    includeLink: true,
                    includeIcon: true,
                  }),
                ],
                showIf: `options.get('hasFooterMenuItems') === true`,
              },
            ],
          },
        ],
      },
      // {
      //   name: "navBar",
      //   type: "reference",
      //   model: 'menu-main',
      //   showIf: `options.get('showNav') === true`,
      // },
    ],
  });

  // Footer
  Builder.registerComponent(Footer, {
    name: "Footer",
    noWrap: true,
    hideFromInsertMenu: true,
    inputs: [
      {
        name: "darkMode",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "variant",
        type: "string",
        enum: sectionVariants,
        defaultValue: "A",
      },
      {
        name: "spacing",
        type: "string",
        enum: sectionSpacing,
      },
      {
        name: "withCtaBanner",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "ctaBanner",
        type: "object",
        subFields: [
          { 
            name: "title", 
            type: "string", 
            defaultValue: "Are you ready to power your business?" 
          },
          { 
            name: "subtitle", 
            type: "string",
            defaultValue: "Join thousands of businesses already leveraging our working capital solutions to achieve more." 
          },
          { 
            name: "buttonText", 
            type: "string", 
            defaultValue: "Button Text" 
          },
          { 
            name: "buttonLink", 
            type: "url", 
            defaultValue: "/" 
          },
          { 
            name: "buttonStyle",  
            type: "string", 
            enum: ["primary", "secondary", "brand"], 
            defaultValue: "secondary" 
          },
        ],
        showIf: `options.get('withCtaBanner') === true`,
      },
      {
        name: "withStayConnected",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "stayConnected",
        type: "object",
        subFields: [
          {
            name: "title",
            type: "string",
            defaultValue: "Stay Connected"
          },
          {
            name: "subtitle",
            type: "string",
            defaultValue: "Stay connected with us to get the latest news and updates."
          },
          {
            name: "showLinkedIn",
            type: "boolean",
            defaultValue: true,
          },
          {
            name: "linkedInLink",
            type: "url",
            defaultValue: "/",
            showIf: `options.get('showLinkedIn') === true`,
          },
        ],
        showIf: `options.get('withStayConnected') === true`,
      },
      {
        name: "withFooterMenu",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "footerMenu",
        type: "list",
        subFields: [
          { 
            name: "title", 
            type: "string" 
          },
          {
            name: "link",
            type: "url",
          },
          { 
            name: "menuItem", 
            type: "list", 
            subFields: [
              {
                name: "label",
                type: "string",
              },
              {
                name: "link",
                type: "url",
              },
            ],
          },
        ],
        showIf: `options.get('withFooterMenu') === true`,
      },
      {
        name: "withCopyright",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "copyright",
        type: "string",
        defaultValue: "Copyright © 2024",
        showIf: `options.get('withCopyright') === true`,
      },
      {
        name: "policyLinks",
        type: "list",
        subFields: [
          {
            name: "label",
            type: "string",
          },
          {
            name: "link",
            type: "url",
          },
        ],
        showIf: `options.get('withCopyright') === true`,
      },
      
    ],  
  });

  //Register of components below
  Builder.registerComponent(CustomBreadcrumb, {
    name: "Breadcrumb",
    noWrap: true,
    inputs: [
      {
        name: "darkMode",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "variant",
        type: "string",
        enum: sectionVariants,
        defaultValue: "A",
      },
      {
        name: "level1",
        type: "object",
        defaultValue: { override: false },
        subFields: [
          { name: "override", type: "boolean", defaultValue: false },
          {
            name: "customLabel",
            type: "string",
            helperText: "Label override for Level 1",
            showIf: `options.get('override') === true`,
          },
          {
            name: "customHref",
            type: "string",
            helperText: "Link override for Level 1",
            showIf: `options.get('override') === true`,
          },
        ],
      },
      {
        name: "level2",
        type: "object",
        defaultValue: { override: false },
        subFields: [
          { name: "override", type: "boolean", defaultValue: false },
          {
            name: "customLabel",
            type: "string",
            helperText: "Label override for Level 2",
            showIf: `options.get('override') === true`,
          },
          {
            name: "customHref",
            type: "string",
            helperText: "Link override for Level 2",
            showIf: `options.get('override') === true`,
          },
        ],
      },
      {
        name: "level3",
        type: "object",
        defaultValue: { override: false },
        subFields: [
          { name: "override", type: "boolean", defaultValue: false },
          {
            name: "customLabel",
            type: "string",
            helperText: "Label override for Level 3",
            showIf: `options.get('override') === true`,
          },
          {
            name: "customHref",
            type: "string",
            helperText: "Link override for Level 3",
            showIf: `options.get('override') === true`,
          },
        ],
      },
      {
        name: "level4",
        type: "object",
        defaultValue: { override: false },
        subFields: [
          { name: "override", type: "boolean", defaultValue: false },
          {
            name: "customLabel",
            type: "string",
            helperText: "Label override for Level 4",
            showIf: `options.get('override') === true`,
          },
          {
            name: "customHref",
            type: "string",
            helperText: "Link override for Level 4",
            showIf: `options.get('override') === true`,
          },
        ],
      },
    ],
  });

  Builder.registerComponent(PageNav, {
    name: 'PageNav',
    noWrap: true,
    canHaveChildren: false,
    inputs: [...commonInputs.baseSettings({
      includeSpacing: false,
      includePageNav: true,
    })],
  });

  Builder.registerComponent(NotFound, {
    name: "Not Found",
    noWrap: true,
    inputs: [
      ...Object.values(commonInputs.baseHero),
      {
        name: "contents",
        type: "object",
        defaultValue: {
          badge: "Badge",
          title: "Title",
          content: "Content",
          microCopy: "Microcopy",
          image: "/placeholder.svg",
          imageAltText: "Image Alt Text",
          hasButton: false,
          buttonText: "Button Text",
          buttonLink: "/",
          buttonStyle: "primary",
          secondaryButton: false,
          secondaryButtonText: "Secondary Button Text",
          secondaryButtonLink: "/",
          secondaryButtonStyle: "primary",
        },
        subFields: [
          ...commonInputs.contentInputs({
            includeBadge: true,
            includeTitle: true,
            includeContent: true,
          }),
          ...commonInputs.buttonInputs({excludeMicroCopy: false}),
          ...commonInputs.secondaryButtonInputs(),
          ...commonInputs.imageInput({
            name: "image",
            includeAltText: true,
          }),
        ],
      },
    ],
  }); 
}
